import { useState } from 'react';
import { motion } from 'framer-motion';
import { Palette, Shapes, Image, Frame, Settings2, Sparkles, Wand2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useQRStore } from '@/stores/qrStore';
import { ColorsTab } from './ColorsTab';
import { ShapeTab } from './ShapeTab';
import { LogoTab } from './LogoTab';
import { FrameTab } from './FrameTab';
import { AdvancedTab } from './AdvancedTab';
import { ArtisticAITab } from './ArtisticAITab';
import { AISuggestModal } from './AISuggestModal';

const tabs = [
  { id: 'colors', label: 'Colors', icon: Palette },
  { id: 'shape', label: 'Shape', icon: Shapes },
  { id: 'logo', label: 'Logo', icon: Image },
  { id: 'frame', label: 'Frame', icon: Frame },
  { id: 'advanced', label: 'Advanced', icon: Settings2 },
  { id: 'ai-art', label: 'Artistic AI', icon: Wand2 },
];

export function DesignTabs() {
  const { activeDesignTab, setActiveDesignTab } = useQRStore();
  const [aiModalOpen, setAiModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      {/* AI Suggest Button */}
      <Button
        onClick={() => setAiModalOpen(true)}
        variant="outline"
        className="w-full bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20 hover:border-primary/40 transition-all"
      >
        <Sparkles className="mr-2 h-4 w-4 text-primary" />
        AI Suggest Design
      </Button>

      <AISuggestModal open={aiModalOpen} onOpenChange={setAiModalOpen} />
      
      <Tabs value={activeDesignTab} onValueChange={setActiveDesignTab} className="w-full">
      <div className="relative">
        {/* Horizontal scroll for mobile */}
        <ScrollArea className="w-full" type="scroll">
          <TabsList className="w-full inline-flex h-12 items-center justify-start gap-1 rounded-xl bg-muted/50 p-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeDesignTab === tab.id;

              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-lg whitespace-nowrap"
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute inset-0 rounded-lg bg-background shadow-sm -z-10"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                    />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </ScrollArea>
      </div>

      <div className="mt-4">
        <TabsContent value="colors" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ColorsTab />
          </motion.div>
        </TabsContent>

        <TabsContent value="shape" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ShapeTab />
          </motion.div>
        </TabsContent>

        <TabsContent value="logo" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <LogoTab />
          </motion.div>
        </TabsContent>

        <TabsContent value="frame" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FrameTab />
          </motion.div>
        </TabsContent>

        <TabsContent value="advanced" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <AdvancedTab />
          </motion.div>
        </TabsContent>

        <TabsContent value="ai-art" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ArtisticAITab />
          </motion.div>
        </TabsContent>
      </div>
      </Tabs>
    </div>
  );
}
